import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, TrendingUp, ShoppingBag, Signal, DollarSign, FileText, LogOut, HardDrive, Database, CreditCard, MessageSquare, Ticket, Activity, Crosshair } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('borsamex_token');
  const [pendingPaymentsCount, setPendingPaymentsCount] = useState(0);

  // Fetch pending payments count
  useEffect(() => {
    const fetchPendingPayments = async () => {
      try {
        const res = await axios.get('/membership/admin/pending-payments');
        const pending = res.data.pending_payments || [];
        setPendingPaymentsCount(pending.length);
      } catch (err) {
        console.error('Failed to fetch pending payments:', err);
      }
    };
    
    fetchPendingPayments();
    // Refresh every 60 seconds
    const interval = setInterval(fetchPendingPayments, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/payments', icon: CreditCard, label: 'Ödemeler', showBadge: 'payments' },
    { path: '/admin/tickets', icon: Ticket, label: 'Destek Talepleri' },
    { path: '/admin/contact', icon: MessageSquare, label: 'İletişim' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/scanner', icon: Activity, label: 'AI Tarama' },
    { path: '/admin/contrarian', icon: Crosshair, label: 'Contrarian' },
    { path: '/admin/signals', icon: Signal, label: 'Signals' },
    { path: '/admin/storage', icon: HardDrive, label: 'Storage' },
    { path: '/admin/backup', icon: Database, label: 'Backups' },
    { path: '/admin/strategies', icon: TrendingUp, label: 'Strategies' },
    { path: '/admin/marketplace', icon: ShoppingBag, label: 'Marketplace' },
    { path: '/admin/revenue', icon: DollarSign, label: 'Revenue' },
    { path: '/admin/logs', icon: FileText, label: 'System Logs' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-brand-dark flex">
      {/* Sidebar */}
      <div className="w-64 bg-brand-card border-r border-border/40 flex flex-col">
        {/* Logo/Header */}
        <div className="p-6 border-b border-border/40">
          <h1 className="text-xl font-bold text-brand-green">Borsamex</h1>
          <p className="text-[10px] text-slate-500 mt-0.5">Admin Panel</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            const hasPendingBadge = item.showBadge === 'payments' && pendingPaymentsCount > 0;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  active
                    ? 'bg-brand-green/20 text-brand-green font-semibold'
                    : hasPendingBadge 
                      ? 'text-yellow-400 hover:bg-yellow-900/20 hover:text-yellow-300'
                      : 'text-slate-400 hover:bg-brand-card-hover hover:text-slate-200'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
                {hasPendingBadge && !active && (
                  <span className="ml-auto flex items-center gap-1.5">
                    <span className="text-xs bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded-full font-bold">
                      {pendingPaymentsCount}
                    </span>
                    <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border/40">
          <Link to="/dashboard" className="block mb-2">
            <Button variant="outline" className="w-full" size="sm">
              Back to User App
            </Button>
          </Link>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full text-red-400 hover:bg-red-900/20"
            size="sm"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
};

export default AdminLayout;
